import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Briefcase, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';

const G = '#0F5A3A';
const GOLD = '#F5B327';
const IVORY = '#F4F0E6';
const BTN_R = '8px';

const PARTNERS = [
  { icon: <Building2 size={22} color={G} />, title: 'For Colleges', desc: 'Connect with qualified, motivated students who are actively searching for the right fit. Direct access to student profiles and engagement data.', points: ['Direct student outreach tools','Enrollment signal analytics','Profile-based targeting'], cta: 'Partner as a College', href: '/for-schools' },
  { icon: <Briefcase size={22} color={G} />, title: 'For Employers', desc: 'Reach the next generation of professionals before graduation. Post internships, first jobs, and career pathways to students actively planning their futures.', points: ['Post internships & entry-level roles','Signal match for job readiness','Early talent brand visibility'], cta: 'Partner as an Employer', href: '/partnerships' },
  { icon: <Users size={22} color={G} />, title: 'For Community Partners', desc: 'Nonprofits, foundations, and civic organizations can surface scholarships, programs, and mentorship to verified, engaged student audiences.', points: ['Scholarship listing & promotion','Program & event visibility','Student community access'], cta: 'Partner with Us', href: '/partnerships' },
];

export const PartnershipsSection = () => {
  const isMobile = useIsMobile();

  return (
    <section style={{ background: '#fff', padding: isMobile ? '48px 0' : '72px 0' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 40px' }}>
        <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}
          style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '48px', alignItems: 'end', marginBottom: isMobile ? '28px' : '48px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: GOLD, marginBottom: '10px' }}>Partner with Student Signal</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1.75rem' : 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: 900, color: G, lineHeight: 1.1, letterSpacing: '-0.02em', margin: 0 }}>
              Free for students.<br />Powerful for partners.
            </h2>
          </div>
          <p style={{ fontSize: '15px', color: '#6E6A61', lineHeight: 1.75, margin: 0 }}>
            Student Signal gives institutions and employers a direct channel to engaged, motivated students at the exact moment they&apos;re making decisions.
          </p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
          {PARTNERS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: i * 0.1 }}
              style={{ background: IVORY, border: '1px solid #e8e4df', borderRadius: '14px', padding: isMobile ? '22px' : '28px', display: 'flex', flexDirection: 'column' }}
              data-testid={`partner-card-${p.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div style={{ width: '44px', height: '44px', borderRadius: '11px', background: `${G}0e`, border: `1px solid ${G}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>{p.icon}</div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.15rem', fontWeight: 800, color: G, marginBottom: '8px' }}>{p.title}</h3>
              <p style={{ fontSize: '13px', color: '#6E6A61', lineHeight: 1.65, marginBottom: '14px', flex: 1 }}>{p.desc}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
                {p.points.map(pt => (
                  <li key={pt} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px', fontSize: '13px', color: '#4A5568' }}>
                    <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: G, flexShrink: 0 }} />{pt}
                  </li>
                ))}
              </ul>
              <Link to={p.href} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '10px 16px', background: G, color: '#fff', fontWeight: 700, fontSize: '12px', borderRadius: BTN_R, textDecoration: 'none', alignSelf: 'flex-start' }}>
                {p.cta} <ArrowRight size={12} />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnershipsSection;
